<!-- superadmin-company-detail.component.html -->

<div class="sacd">

  <!-- ── Loading ──────────────────────────────────── -->
  <div class="sacd__loading" *ngIf="loading">
    <div class="sacd__spinner"></div>
    <span>Loading company data…</span>
  </div>

  <ng-container *ngIf="!loading && company">

    <!-- ── Header ────────────────────────────────── -->
    <div class="sacd__header">
      <div class="sacd__header-left">
        <a routerLink="/superadmin/companies" class="sacd__back-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12L12 19M5 12L12 5"
                  stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Companies
        </a>
        <div class="sacd__header-divider"></div>
        <div class="sacd__header-title-wrap">
          <div class="sacd__big-avatar">
            {{ company.name.charAt(0).toUpperCase() }}
          </div>
          <div>
            <h1 class="sacd__title">{{ company.name }}</h1>
            <p class="sacd__subtitle">
              Joined {{ company.createdUtc | date:'MMMM d, y' }}
              · ID: {{ company.id | slice:0:8 }}…
            </p>
          </div>
        </div>
      </div>

      <!-- Status + actions -->
      <div class="sacd__header-actions">
        <span class="sacd__status-badge"
              [class.sacd__status-badge--active]="isActive"
              [class.sacd__status-badge--suspended]="!isActive">
          <span class="sacd__status-dot"></span>
          {{ isActive ? 'Active' : 'Suspended' }}
        </span>

        <button *ngIf="isActive"
                class="sacd__action-btn sacd__action-btn--suspend"
                (click)="openConfirm('suspend')">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"
                  stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
          Suspend Company
        </button>

        <button *ngIf="!isActive"
                class="sacd__action-btn sacd__action-btn--reactivate"
                (click)="openConfirm('reactivate')">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Reactivate
        </button>
      </div>
    </div>

    <!-- ── Summary Strip ─────────────────────────── -->
    <div class="sacd__strip">
      <div class="sacd__strip-item">
        <span class="sacd__strip-val">{{ company.workers.length }}</span>
        <span class="sacd__strip-label">Workers</span>
      </div>
      <div class="sacd__strip-sep"></div>
      <div class="sacd__strip-item">
        <span class="sacd__strip-val">{{ company.admins.length }}</span>
        <span class="sacd__strip-label">Admins</span>
      </div>
      <div class="sacd__strip-sep"></div>
      <div class="sacd__strip-item">
        <span class="sacd__strip-val">{{ company.jobs.length }}</span>
        <span class="sacd__strip-label">Jobs</span>
      </div>
      <div class="sacd__strip-sep"></div>
      <div class="sacd__strip-item">
        <span class="sacd__strip-val">{{ company.recentLogs.length }}</span>
        <span class="sacd__strip-label">Recent Logs</span>
      </div>
    </div>

    <!-- ── Body Grid ──────────────────────────────── -->
    <div class="sacd__grid">

      <!-- ── LEFT: Workers + Admins ──────────────── -->
      <div class="sacd__left">

        <!-- Workers -->
        <div class="sacd__panel">
          <div class="sacd__panel-head">
            <div class="sacd__panel-icon sacd__panel-icon--blue">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.8"/>
                <path d="M2 20C2 17 5 14 9 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                <circle cx="17" cy="10" r="3" stroke="currentColor" stroke-width="1.8"/>
              </svg>
            </div>
            <h3 class="sacd__panel-title">Workers</h3>
            <span class="sacd__panel-count">{{ company.workers.length }}</span>
          </div>

          <div class="sacd__user-empty" *ngIf="company.workers.length === 0">
            No workers registered.
          </div>

          <div class="sacd__user-list" *ngIf="company.workers.length > 0">
            <div class="sacd__user-row" *ngFor="let w of company.workers">
              <div class="sacd__user-avatar sacd__user-avatar--blue">
                {{ getInitials(w.fullName) }}
              </div>
              <div class="sacd__user-info">
                <span class="sacd__user-name">{{ w.fullName }}</span>
                <span class="sacd__user-email">{{ w.email }}</span>
              </div>
              <span class="sacd__user-status"
                    [class.sacd__user-status--active]="w.isActive"
                    [class.sacd__user-status--inactive]="!w.isActive">
                {{ w.isActive ? 'Active' : 'Inactive' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Admins -->
        <div class="sacd__panel">
          <div class="sacd__panel-head">
            <div class="sacd__panel-icon sacd__panel-icon--gold">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                      stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3 class="sacd__panel-title">Admins</h3>
            <span class="sacd__panel-count">{{ company.admins.length }}</span>
          </div>

          <div class="sacd__user-empty" *ngIf="company.admins.length === 0">
            No admins found.
          </div>

          <div class="sacd__user-list" *ngIf="company.admins.length > 0">
            <div class="sacd__user-row" *ngFor="let a of company.admins">
              <div class="sacd__user-avatar sacd__user-avatar--gold">
                {{ getInitials(a.fullName) }}
              </div>
              <div class="sacd__user-info">
                <span class="sacd__user-name">{{ a.fullName }}</span>
                <span class="sacd__user-email">{{ a.email }}</span>
              </div>
              <span class="sacd__user-status"
                    [class.sacd__user-status--active]="a.isActive"
                    [class.sacd__user-status--inactive]="!a.isActive">
                {{ a.isActive ? 'Active' : 'Inactive' }}
              </span>
            </div>
          </div>
        </div>

      </div>

      <!-- ── RIGHT: Jobs + Logs ───────────────────── -->
      <div class="sacd__right">

        <!-- Recent Jobs -->
        <div class="sacd__panel">
          <div class="sacd__panel-head">
            <div class="sacd__panel-icon sacd__panel-icon--green">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/>
                <path d="M16 7V5C16 3.9 15.1 3 14 3H10C8.9 3 8 3.9 8 5V7"
                      stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </div>
            <h3 class="sacd__panel-title">Recent Jobs</h3>
            <span class="sacd__panel-count">{{ company.jobs.length }}</span>
          </div>

          <div class="sacd__user-empty" *ngIf="company.jobs.length === 0">
            No jobs created yet.
          </div>

          <div class="sacd__jobs-list" *ngIf="company.jobs.length > 0">
            <div class="sacd__job-row" *ngFor="let j of company.jobs">
              <div class="sacd__job-icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/>
                  <path d="M16 7V5C16 3.9 15.1 3 14 3H10C8.9 3 8 3.9 8 5V7"
                        stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </div>
              <div class="sacd__job-info">
                <span class="sacd__job-title">{{ j.title }}</span>
                <span class="sacd__job-date">{{ j.createdUtc | date:'MMM d, y' }}</span>
              </div>
              <span class="sacd__job-status"
                    [class.sacd__job-status--pending]="j.status === 0"
                    [class.sacd__job-status--progress]="j.status === 1"
                    [class.sacd__job-status--complete]="j.status === 2"
                    [class.sacd__job-status--failed]="j.status === 3">
                {{ getStatusLabel(j.status) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Recent Logs -->
        <div class="sacd__panel">
          <div class="sacd__panel-head">
            <div class="sacd__panel-icon sacd__panel-icon--teal">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                      stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </div>
            <h3 class="sacd__panel-title">Recent Logs</h3>
            <span class="sacd__panel-count">{{ company.recentLogs.length }}</span>
          </div>

          <div class="sacd__user-empty" *ngIf="company.recentLogs.length === 0">
            No logs submitted yet.
          </div>

          <div class="sacd__logs-list" *ngIf="company.recentLogs.length > 0">
            <div class="sacd__log-row" *ngFor="let l of company.recentLogs">
              <div class="sacd__log-avatar"
                   [style.background]="getAvatarColor(l.userId)">
                {{ getInitials(getWorkerName(l.userId)) }}
              </div>
              <div class="sacd__log-info">
                <span class="sacd__log-name">{{ getWorkerName(l.userId) }}</span>
                <span class="sacd__log-date">{{ l.createdUtc | date:'MMM d, h:mm a' }}</span>
              </div>
              <span class="sacd__log-hours">{{ l.hoursSpent }}h</span>
            </div>
          </div>
        </div>

      </div>
    </div>

  </ng-container>

  <!-- ── Confirm Modal ────────────────────────────── -->
  <div class="sacd__modal-overlay" *ngIf="confirmAction" (click)="closeConfirm()">
    <div class="sacd__modal" (click)="$event.stopPropagation()">

      <div class="sacd__modal-icon"
           [class.sacd__modal-icon--danger]="confirmAction === 'suspend'"
           [class.sacd__modal-icon--success]="confirmAction === 'reactivate'">
        <svg *ngIf="confirmAction === 'suspend'" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"
                stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
        <svg *ngIf="confirmAction === 'reactivate'" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <h3 class="sacd__modal-title">
        {{ confirmAction === 'suspend' ? 'Suspend Company?' : 'Reactivate Company?' }}
      </h3>
      <p class="sacd__modal-desc">
        <strong>{{ company?.name }}</strong>
        {{ confirmAction === 'suspend'
          ? ' and all its users will be deactivated immediately.'
          : ' and all its users will be restored and able to log in again.' }}
      </p>

      <div class="sacd__modal-actions">
        <button class="sacd__modal-cancel" (click)="closeConfirm()">Cancel</button>
        <button class="sacd__modal-confirm"
                [class.sacd__modal-confirm--danger]="confirmAction === 'suspend'"
                [class.sacd__modal-confirm--success]="confirmAction === 'reactivate'"
                (click)="executeAction()"
                [disabled]="acting">
          <span class="sacd__btn-spinner" *ngIf="acting"></span>
          {{ acting ? 'Processing…' : (confirmAction === 'suspend' ? 'Suspend' : 'Reactivate') }}
        </button>
      </div>

    </div>
  </div>

</div>